import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  Search,
  Phone,
  ArrowRight,
  Heart,
  Users,
  Clock,
  DollarSign,
  CheckCircle,
  Shield,
  Brain,
  Sparkles,
  Leaf,
  Activity,
  Calculator,
  MapPin,
  BookOpen
} from "lucide-react";
import { Header, Footer } from "../../../components/Layout";

const ResidencialPage = () => {
  const therapeuticApproaches = [
    {
      name: "Terapia Cognitiva",
      description: "Se enfoca en identificar y modificar los pensamientos distorsionados que mantienen la adicción.",
      focus: "Pensamientos y creencias",
      icon: Brain,
      color: "text-purple-400",
      bgColor: "bg-purple-500/10",
      borderColor: "border-purple-500/30",
      path: "/tratamiento/residencial/cognitivo",
      features: ["Reestructuración cognitiva", "Identificación de distorsiones", "Trabajo con creencias centrales"]
    },
    {
      name: "Terapia Conductual",
      description: "Modifica comportamientos adictivos a través de técnicas de condicionamiento y refuerzo.",
      focus: "Comportamientos y hábitos",
      icon: Activity,
      color: "text-orange-400",
      bgColor: "bg-orange-500/10",
      borderColor: "border-orange-500/30",
      path: "/tratamiento/residencial/conductual",
      features: ["Economía de fichas", "Prevención de recaídas", "Manejo de contingencias"]
    },
    {
      name: "Cognitivo-Conductual (TCC)",
      description: "Combina lo mejor de ambos enfoques. Es el estándar de oro en tratamiento de adicciones.",
      focus: "Pensamientos y comportamientos",
      icon: Sparkles,
      color: "text-emerald-400",
      bgColor: "bg-emerald-500/10",
      borderColor: "border-emerald-500/30",
      path: "/tratamiento/residencial/cognitivo-conductual",
      features: ["Mayor evidencia científica", "Resultados a largo plazo", "Prevención de recaídas"]
    },
    {
      name: "Enfoque Holístico",
      description: "Trata la adicción desde todas las dimensiones: física, mental, emocional y espiritual.",
      focus: "Persona integral",
      icon: Leaf,
      color: "text-green-400",
      bgColor: "bg-green-500/10",
      borderColor: "border-green-500/30",
      path: "/tratamiento/residencial/holistico",
      features: ["Mindfulness y meditación", "Yoga y ejercicio", "Nutrición y autocuidado"]
    },
    {
      name: "Enfoque Integrativo",
      description: "Combina múltiples modelos terapéuticos adaptados a cada persona.",
      focus: "Personalización total",
      icon: Heart,
      color: "text-pink-400",
      bgColor: "bg-pink-500/10",
      borderColor: "border-pink-500/30",
      path: "/tratamiento/residencial/integrativo",
      features: ["Evaluación personalizada", "Múltiples técnicas", "Adaptación continua"]
    }
  ];

  const benefits = [
    { title: "Ambiente controlado 24/7", description: "Libre de sustancias y triggers, con supervisión constante" },
    { title: "Atención multidisciplinaria", description: "Psiquiatras, psicólogos, terapeutas, médicos y nutricionistas" },
    { title: "Estructura y rutina", description: "Horarios definidos que crean nuevos hábitos saludables" },
    { title: "Comunidad terapéutica", description: "Apoyo de pares que entienden tu situación" },
    { title: "Desconexión del entorno", description: "Alejamiento de personas y lugares de consumo" },
    { title: "Mayor tasa de éxito", description: "70-85% de efectividad vs 60-70% de ambulatorio" }
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    "name": "Tratamiento Residencial para Adicciones",
    "description": "Internación en centro especializado con atención 24/7 para tratamiento de adicciones severas.",
    "relevantSpecialty": {
      "@type": "MedicalSpecialty",
      "name": "Psiquiatría de Adicciones"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Tratamientos", "item": "https://sinadicciones.org/tratamientos" },
      { "@type": "ListItem", "position": 3, "name": "Residencial", "item": "https://sinadicciones.org/tratamiento/residencial" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Tratamiento Residencial para Adicciones: Internación 24/7 | Sin Adicciones Chile</title>
        <meta name="description" content="Tratamiento residencial con internación para adicciones en Chile. Enfoques cognitivo, conductual, holístico e integrativo. Desde $1.500.000/mes. Compara centros." />
        <meta property="og:title" content="Tratamiento Residencial para Adicciones" />
        <meta property="og:description" content="Internación con atención 24/7. Múltiples enfoques terapéuticos: cognitivo, conductual, holístico e integrativo." />
        <link rel="canonical" href="https://sinadicciones.org/tratamiento/residencial" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/tratamiento/residencial" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/tratamientos" className="hover:text-emerald-400 transition-colors">
              Tratamientos
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Residencial</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                <Shield className="w-7 h-7 text-emerald-400" />
              </div>
              <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
                <span className="text-emerald-400 font-medium">Mayor efectividad</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Tratamiento{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
                Residencial
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl">
              Internación en centro especializado con atención 24/7. 
              La opción más efectiva para adicciones severas, con múltiples enfoques 
              terapéuticos adaptados a cada persona.
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <DollarSign className="w-5 h-5 text-emerald-400 mb-2" />
                <p className="text-white font-bold">$1.5M - $4.5M</p>
                <p className="text-slate-400 text-sm">por mes</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Clock className="w-5 h-5 text-emerald-400 mb-2" />
                <p className="text-white font-bold">1-12 meses</p>
                <p className="text-slate-400 text-sm">duración típica</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Shield className="w-5 h-5 text-emerald-400 mb-2" />
                <p className="text-white font-bold">24/7</p>
                <p className="text-slate-400 text-sm">supervisión</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Heart className="w-5 h-5 text-emerald-400 mb-2" />
                <p className="text-white font-bold">70-85%</p>
                <p className="text-slate-400 text-sm">efectividad</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile?modality=residencial"
                className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Buscar Centros Residenciales
              </Link>
              <Link 
                to="/calculadora"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                Calcular Costo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              ¿Por qué elegir Tratamiento Residencial?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              La internación ofrece el ambiente más seguro y efectivo para la recuperación.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-emerald-500/20"
              >
                <CheckCircle className="w-8 h-8 text-emerald-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                <p className="text-slate-400">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Therapeutic Approaches */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Enfoques Terapéuticos
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Los centros residenciales utilizan diferentes modelos de tratamiento. 
              Conoce cada uno para elegir el que mejor se adapte a ti.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {therapeuticApproaches.map((approach, index) => (
              <Link 
                key={index}
                to={approach.path}
                className={`group p-6 rounded-2xl ${approach.bgColor} border ${approach.borderColor} hover:border-opacity-60 transition-all`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-xl ${approach.bgColor} flex items-center justify-center`}>
                    <approach.icon className={`w-6 h-6 ${approach.color}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-xl font-bold ${approach.color}`}>{approach.name}</h3>
                    <p className="text-slate-500 text-sm">Enfoque: {approach.focus}</p>
                  </div>
                  <ArrowRight className={`w-5 h-5 ${approach.color} opacity-0 group-hover:opacity-100 transition-opacity`} />
                </div>
                
                <p className="text-slate-400 text-sm mb-4">{approach.description}</p>
                
                <div className="space-y-2">
                  {approach.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-slate-300 text-sm">
                      <CheckCircle className={`w-4 h-4 ${approach.color} flex-shrink-0`} />
                      {feature}
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-slate-700/50">
                  <span className={`text-sm font-medium ${approach.color} flex items-center gap-1`}>
                    Conocer más sobre este enfoque
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-slate-400 mb-4">¿No sabes cuál es el mejor para ti?</p>
            <Link 
              to="/asesoria"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-medium rounded-xl hover:bg-emerald-500/20 transition-all"
            >
              <Phone className="w-5 h-5" />
              Recibe asesoría experta de nuestros especialistas
            </Link>
          </div>
        </div>
      </section>

      {/* Duration Guide */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-3xl p-8 md:p-12 border border-slate-700/50">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">
              ¿Cuánto tiempo de internación necesito?
            </h2>
            
            <div className="space-y-4">
              {[
                { duration: "1-3 meses", severity: "Adicción moderada", description: "Primer tratamiento, sin comorbilidades graves, buen entorno de apoyo" },
                { duration: "3-6 meses", severity: "Adicción severa", description: "Historial de recaídas, comorbilidades leves, necesidad de estabilización" },
                { duration: "6-12 meses", severity: "Adicción muy severa", description: "Múltiples sustancias, comorbilidades psiquiátricas, entorno de alto riesgo" }
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-slate-800/50 rounded-xl">
                  <div className="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-sm font-bold rounded-full whitespace-nowrap">
                    {item.duration}
                  </div>
                  <div>
                    <p className="text-white font-semibold">{item.severity}</p>
                    <p className="text-slate-400 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-6 text-center text-slate-400 text-sm">
              La duración exacta se determina mediante evaluación profesional. 
              Tratamientos más largos tienen mayor tasa de éxito a largo plazo.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-900/50 to-slate-800/50 border border-emerald-500/20 p-8 md:p-12 text-center">
            <Shield className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Encuentra tu Centro Residencial
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Compara más de 100 centros residenciales en Chile. 
              Filtra por enfoque terapéutico, ubicación y precio.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile?modality=residencial"
                className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/25 flex items-center gap-2"
              >
                <MapPin className="w-5 h-5" />
                Ver Centros Residenciales
              </Link>
              <Link 
                to="/asesoria"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Asesoría Experta
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Otros Tipos de Tratamiento</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Ambulatorio", path: "/tratamiento/ambulatorio", icon: "🏢" },
              { title: "Online", path: "/tratamiento/online", icon: "💻" },
              { title: "Desintoxicación", path: "/tratamiento/desintoxicacion", icon: "🏨" },
              { title: "Grupos de Apoyo", path: "/tratamiento/grupos-apoyo", icon: "👥" }
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-emerald-500/30 transition-all group"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-white group-hover:text-emerald-400 transition-colors">{item.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResidencialPage;
